import TransactionStatus from "enums/TransactionStatus";
import TransactionsType from "enums/TransactionsType";
import ViolationType from "enums/ViolationType";

/**
 * The type and status groupings the admin ledger filters by, in one place
 * because the Transactions page and the Reimbursements page slice the same
 * `transactions` query and previously kept separate copies of these lists.
 */

/** What a dispute returns and takes back, beside the purchase it never changes. */
export const DISPUTE_MONEY_TYPES = [
  TransactionsType.DISPUTE_CREDIT,
  TransactionsType.DISPUTE_CREDIT_REVERSAL,
];

/** Card spend. REFUND and dispute money ride along: each moves money back on a card capture. */
export const CARD_TYPES = [
  TransactionsType.CAPTURE,
  TransactionsType.AUTHORIZATION,
  TransactionsType.REFUND,
  ...DISPUTE_MONEY_TYPES,
];

/** Out-of-pocket spend an employee fronts and the company pays back. */
export const REIMBURSEMENT_TYPES = [
  TransactionsType.CASH,
  TransactionsType.MILEAGE,
];

/** What can owe a receipt or an allocation. The dashboard's server-side counts leave out AUTHORIZATION. */
export const COMPLIANCE_TYPES = [
  TransactionsType.CAPTURE,
  TransactionsType.AUTHORIZATION,
  ...REIMBURSEMENT_TYPES,
];

/** Money moving between the company's own accounts. */
export const TRANSFER_TYPES = [
  TransactionsType.INBOUND_TRANSFER,
  TransactionsType.OUTBOUND_TRANSFER,
  TransactionsType.INBOUND_REWARDS,
  TransactionsType.REWARDS_TO_BALANCE,
];

/** Money movement. A reimbursement is a request, and its OUTBOUND_TRANSFER payout is the ledger row. */
export const LEDGER_TYPES = [
  TransactionsType.AUTHORIZATION,
  TransactionsType.CAPTURE,
  TransactionsType.INBOUND_PAYMENT,
  TransactionsType.INBOUND_TRANSFER,
  TransactionsType.INBOUND_WIRE,
  TransactionsType.OUTBOUND_PAYMENT,
  TransactionsType.OUTBOUND_TRANSFER,
  TransactionsType.OUTBOUND_WIRE,
  TransactionsType.REFUND,
  TransactionsType.INBOUND_FUNDS,
  TransactionsType.REFUND_PAYMENT,
  TransactionsType.FEE,
  TransactionsType.CHARGE_REPAYMENT,
  ...DISPUTE_MONEY_TYPES,
];

/**
 * What the spend totals count. A reimbursement is spend at its CASH or MILEAGE
 * row, and its payout is an OUTBOUND_TRANSFER linked to that row, so counting
 * transfers here would count every paid reimbursement twice. Dispute money
 * nets against the purchase it returns or takes back.
 */
export const SPEND_TYPES = [
  TransactionsType.AUTHORIZATION,
  TransactionsType.CAPTURE,
  TransactionsType.OUTBOUND_PAYMENT,
  TransactionsType.FEE,
  ...REIMBURSEMENT_TYPES,
  ...DISPUTE_MONEY_TYPES,
];

/**
 * Reimbursement statuses, curated rather than derived: the lifecycle is
 * payment-oriented, so it uses statuses card spend never reaches. In lifecycle
 * order, which is the order the Status filter lists them in.
 */
export const REIMBURSEMENT_STATUSES = [
  TransactionStatus.PENDING,
  TransactionStatus.PENDING_BANK_ACCOUNT,
  TransactionStatus.PENDING_PAYMENT,
  TransactionStatus.PROCESSING_PAYMENT,
  TransactionStatus.INSUFFICIENT_FUNDS,
  TransactionStatus.PAYMENT_FAIL,
  TransactionStatus.COMPLETE,
  TransactionStatus.DECLINED,
  TransactionStatus.CANCELED,
  TransactionStatus.REVERSED,
];

/**
 * A reimbursement still owed to the employee, from submission until paid. One
 * list for every count of them: the dashboard once counted only two of these,
 * so anyone waiting on a bank account saw zero there (OUT-1546).
 */
export const OUTSTANDING_REIMBURSEMENT_STATUSES = [
  TransactionStatus.PENDING,
  TransactionStatus.PENDING_BANK_ACCOUNT,
  TransactionStatus.PENDING_PAYMENT,
  TransactionStatus.PROCESSING_PAYMENT,
  TransactionStatus.INSUFFICIENT_FUNDS,
  TransactionStatus.PAYMENT_FAIL,
];

/** A canceled or declined request owes nothing until it is resubmitted, so it is never a task, here or in `transactionTasks`. */
export const CLOSED_REQUEST_STATUSES = [
  TransactionStatus.CANCELED,
  TransactionStatus.DECLINED,
];

/** Where a transaction ends up, after which nothing more is owed on it. */
export const TERMINAL_STATUSES = [
  TransactionStatus.DECLINED,
  TransactionStatus.FAILED,
  TransactionStatus.RETURNED,
  TransactionStatus.CANCELED,
  TransactionStatus.REFUNDED,
  TransactionStatus.REVERSED,
];

export const isTerminalStatus = (
  status: unknown,
): status is TransactionStatus =>
  typeof status === "string" &&
  (TERMINAL_STATUSES as readonly string[]).includes(status);

/** A personal expense the employee still owes back, including a repayment that failed. */
export const OUTSTANDING_PERSONAL_STATUSES = [
  TransactionStatus.PERSONAL_PENDING,
  TransactionStatus.PERSONAL_PROCESSING,
  TransactionStatus.PERSONAL_FAILED,
];

/** Spend on a personal card, which only the cardholder's own views list. */
export const PERSONAL_SPEND_STATUSES = [
  TransactionStatus.PERSONAL_PENDING,
  TransactionStatus.PERSONAL_PROCESSING,
  TransactionStatus.PERSONAL_COMPLETE,
  TransactionStatus.PERSONAL_FAILED,
  TransactionStatus.PERSONAL_REFUNDED,
];

/**
 * Every status card spend can reach: derived, not curated. A hand-written list
 * silently drops whatever it forgets, and the states it forgets are the rare
 * ones nobody notices missing. Personal spend is the only exclusion, because
 * that is a real audience distinction rather than an oversight.
 */
export const CARD_SPEND_STATUSES = Object.values(TransactionStatus).filter(
  (status) => !PERSONAL_SPEND_STATUSES.includes(status),
);

export const TRANSFER_STATUSES = [
  TransactionStatus.COMPLETE,
  TransactionStatus.REFUNDED,
  TransactionStatus.DECLINED,
  TransactionStatus.REVERSED,
  TransactionStatus.PENDING,
];

/** What the Tasks tab treats as needing attention. */
export const TASKS_COMPLIANCES = [
  ViolationType.MISSING_RECEIPT,
  ViolationType.MISSING_COST_ALLOCATION,
];

/**
 * What the review tab queues: card spend carrying a receipt or allocation
 * requirement. Reimbursements carry those requirements too, and their queue is
 * the Reimbursements page.
 */
export const TASKS_TYPES = [
  TransactionsType.CAPTURE,
  TransactionsType.AUTHORIZATION,
];

/** What the server's `transactionTasks` counts, so /my's Task tab lists what the dashboard's compliance cards count. */
export const TASK_COUNT_TYPES = [
  TransactionsType.CAPTURE,
  ...REIMBURSEMENT_TYPES,
];

/**
 * The type filter's options, grouped rather than one row per enum member:
 * several types render as one label in the table (a CAPTURE and an
 * AUTHORIZATION are both "Card"), and a filter listing both would offer a
 * distinction the rows never show. Labels are canonical English, translated
 * at the point of render.
 */
export const TYPE_FILTER_GROUPS: ReadonlyArray<{
  id: string;
  label: string;
  types: readonly TransactionsType[];
}> = [
  {
    id: "card",
    label: "Card",
    types: [TransactionsType.AUTHORIZATION, TransactionsType.CAPTURE],
  },
  {
    id: "refund",
    label: "Refund",
    types: [TransactionsType.REFUND, TransactionsType.REFUND_PAYMENT],
  },
  { id: "dispute", label: "Disputes", types: DISPUTE_MONEY_TYPES },
  { id: "ach-in", label: "ACH In", types: [TransactionsType.INBOUND_TRANSFER] },
  {
    id: "ach-out",
    label: "ACH Out",
    types: [TransactionsType.OUTBOUND_TRANSFER],
  },
  {
    id: "repayment",
    label: "Repayment",
    types: [TransactionsType.CHARGE_REPAYMENT],
  },
  {
    id: "payment-in",
    label: "Inbound Payment",
    types: [TransactionsType.INBOUND_PAYMENT, TransactionsType.INBOUND_FUNDS],
  },
  {
    id: "payment-out",
    label: "Outbound Payment",
    types: [TransactionsType.OUTBOUND_PAYMENT],
  },
  { id: "wire-in", label: "Wire In", types: [TransactionsType.INBOUND_WIRE] },
  {
    id: "wire-out",
    label: "Wire Out",
    types: [TransactionsType.OUTBOUND_WIRE],
  },
  {
    id: "rewards-in",
    label: "Inbound Rewards",
    types: [
      TransactionsType.INBOUND_REWARDS,
      TransactionsType.REWARDS_TO_BALANCE,
    ],
  },
  { id: "fee", label: "Fee", types: [TransactionsType.FEE] },
  { id: "cash", label: "Cash", types: [TransactionsType.CASH] },
  { id: "mileage", label: "Mileage", types: [TransactionsType.MILEAGE] },
];

/** Every compliance the filter can ask for, with its canonical English label. */
export const COMPLIANCE_LABELS: Readonly<Record<ViolationType, string>> = {
  [ViolationType.MISSING_RECEIPT]: "Missing receipt",
  [ViolationType.MISSING_COST_ALLOCATION]: "Missing allocation",
  [ViolationType.MISSING_ATTENDEES]: "Missing attendees",
  [ViolationType.UNCATEGORIZED_EXPENSE_CATEGORY]: "Missing category",
};

export const ALL_COMPLIANCES = Object.keys(
  COMPLIANCE_LABELS,
) as ViolationType[];
